import React, { useState, useEffect } from 'react';
import {
  Input,
  Form,
  Button,
  Row,
  Col,
  Select,
  DatePicker,
  Tooltip,
  notification,
  message,
  Checkbox,
} from 'antd';
import styled from 'styled-components';
import dayjs from 'dayjs';
import { webServiceProvider } from 'shared';
import { QuestionCircleOutlined } from '@ant-design/icons';
import { CarStore } from 'stores';
import { CurrentUserStore } from 'stores';
import ShopStore from '../../stores/ShopStore';
import DeviceStore from 'stores/Classes/DeviceStore';
import moment from 'moment';
import ServiceName from './ServiceName';

const { Option } = Select;

const SetupDetailsHeader = styled.h2`
  display: flex;
  align-items: center;
  font-weight: bold;
  font-size: 18px;
  justify-content: flex-start;
  padding-bottom: 30px;

  &::before {
    content: '';
    width: 30px;
    margin-right: 10px;
    height: 1px;
    background-color: #4d79ff;
  }

  &::after {
    content: '';
    flex-grow: 1;
    margin-left: 10px;
    height: 1px;
    background-color: #4d79ff;
  }
`;

const CheckboxGroupWithBorders = styled(Checkbox.Group)`
  display: flex;
  justify-content: space-between;
  width: 100%;

  .ant-checkbox-wrapper {
    text-align: left;
    transition: border-bottom-color 0.3s;
    width: 100%;
  }

  .ant-checkbox-wrapper:hover .ant-checkbox-inner {
    border-color: #4d79ff;
  }

  .ant-checkbox-wrapper.ant-checkbox-wrapper-checked {
    border-bottom-color: transparent;
  }
`;

const HiddenRow = styled(Row)`
  display: ${({ $isVisible }) => ($isVisible ? 'flex' : 'none')};
`;

const BlueLine = styled.div`
  width: 100%;
  height: 2px;
  background-color: ${({ $isActive }) => ($isActive ? '#4d79ff' : '#D9D9D9')};
  margin-top: 8px;
  margin-bottom: 8px;
`;

const ButtonGroup = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding-top: 20px;
`;

const IntervalsRow = styled(Row)`
  width: 100%;
  ${({ $isMobileOrTablet }) =>
    $isMobileOrTablet &&
    `
    flex-direction: column !important;
    > .ant-col {
      width: 100% !important;
      max-width: 100% !important;
      margin-bottom: 16px;
      flex: none !important;
    }
  `}
`;

const StyledConfirmation = styled.div`
  text-align: left;
  font-weight: bold;
  color: rgba(0, 0, 0, 0.85);
`;

const HighlightedText = styled.span`
  text-decoration: underline;
`;

const RULE_TYPE_COMBINATIONS = {
  'time,mileage': 'time_or_mileage',
  'mileage,time': 'time_or_mileage',
  'time,engine_hours': 'time_or_engine_hours',
  'engine_hours,time': 'time_or_engine_hours',
  'mileage,engine_hours': 'mileage_or_engine_hours',
  'engine_hours,mileage': 'mileage_or_engine_hours',
};

const ServiceIntervalConfirmation = ({ time, mileage, hours, isKms }) => {
  const intervals = [];
  if (time) intervals.push(`${time} month${+time > 1 ? 's' : ''}`);
  if (mileage)
    intervals.push(
      `${mileage} ${isKms ? 'km' : 'mile'}${+mileage > 1 ? 's' : ''}`
    );
  if (hours) intervals.push(`${hours} hour${+hours > 1 ? 's' : ''}`);

  if (intervals.length === 1) {
    return (
      <StyledConfirmation>
        Confirmation: Your service interval will repeat every {intervals[0]}
      </StyledConfirmation>
    );
  } else if (intervals.length === 2) {
    return (
      <StyledConfirmation>
        Confirmation: Your service interval will repeat every {intervals[0]}{' '}
        <HighlightedText>OR</HighlightedText> every {intervals[1]}
      </StyledConfirmation>
    );
  }

  return null;
};

const kmToMiles = (km) => (km ? Math.round(km * 0.621371) : 1);
const secondsToHours = (seconds) => (seconds ? Math.round(seconds / 3600) : 1);

const IntervalSelector = ({ initialValues, onCancel }) => {
  const [form] = Form.useForm();
  const userSettings = CurrentUserStore.user.settings;
  const mileOrkm = userSettings?.odometer === 'km' ? 'km' : 'mi';

  const [isEditMode] = useState(Boolean(initialValues?.routineInfo) || false);
  const [serviceHash] = useState(initialValues?.hash);
  const [assets, setAssets] = useState([]);
  const [selectedIntervals, setSelectedIntervals] = useState([]);
  const [formState, setFormState] = useState({
    startedMileage: kmToMiles(initialValues?.routineInfo?.started_mileage),
    startedMileageActual: kmToMiles(
      initialValues?.routineInfo?.started_mileage
    ),
    intervalMileage: kmToMiles(initialValues?.routineInfo?.interval_mileage),
    startedEngineHours: secondsToHours(
      initialValues?.routineInfo?.started_engine_hours
    ),
    startedEngineHoursActual: secondsToHours(
      initialValues?.routineInfo?.started_engine_hours
    ),
    intervalEngineHours: initialValues?.routineInfo?.interval_engine_hours || 1,
    startedTime: initialValues?.routineInfo?.started_time
      ? dayjs.unix(initialValues.routineInfo.started_time)
      : dayjs(),
    repeatInterval: initialValues?.routineInfo?.interval_month || 1,
    assetType:
      initialValues?.car?.carType?.toLowerCase() === 'trailer'
        ? 'Trailer'
        : 'Vehicle',
    selectedAsset: initialValues?.car?.id || null,
  });

  useEffect(() => {
    form.setFieldsValue({
      serviceType: initialValues?.serviceType || 'PM',
      description: initialValues?.description || '',
      assetType: formState.assetType,
      asset: formState.selectedAsset,
      'checkbox-group': [],
      startingMileage: formState.startedMileage,
      intervalMileage: formState.intervalMileage,
      startingEngineHours: formState.startedEngineHours,
      intervalEngineHours: formState.intervalEngineHours,
      serviceStartOn: formState.startedTime,
      ...initialValues,
    });
  }, []);

  const createRuleType = (intervalArray) => {
    if (!Array.isArray(intervalArray) || intervalArray.length === 0)
      return 'time';
    if (intervalArray.length === 1) return intervalArray[0];
    if (intervalArray.length === 2) {
      const key = intervalArray.join(',');
      return RULE_TYPE_COMBINATIONS[key] || intervalArray[0];
    }
    return intervalArray[0];
  };

  useEffect(() => {
    const fetchAssets = async () => {
      try {
        const { result } = await CarStore.getShopCarsBasicInfo(
          ShopStore.currentShop.id
        );
        const filteredAssets = result.filter(
          (asset) => asset.carType === formState.assetType.toLowerCase()
        );
        setAssets(filteredAssets);
      } catch (error) {
        console.error('Error fetching assets:', error);
      }
    };

    fetchAssets();
  }, [formState.assetType]);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const carId = urlParams.get('carId');
    const carType = urlParams.get('carType');

    if (assets.length > 0 && carId) {
      setTimeout(() => {
        if (carType) {
          setFormState((prev) => ({
            ...prev,
            assetType: carType === 'trailer' ? 'Trailer' : 'Vehicle',
          }));
          form.setFieldsValue({
            assetType: carType === 'trailer' ? 'Trailer' : 'Vehicle',
          });
        }
        handleAssetChange(+carId);
      }, 500);
    }
  }, [assets]);

  const handleAssetTypeChange = (value) => {
    setFormState((prev) => ({
      ...prev,
      assetType: value,
      selectedAsset: null,
    }));
    form.setFieldsValue({ asset: undefined });
    const urlParams = new URLSearchParams(window.location.search);
    urlParams.set('carType', value);
    urlParams.delete('carId');
    window.history.replaceState(
      {},
      '',
      `${window.location.pathname}?${urlParams.toString()}`
    );
  };

  const handleAssetChange = (value) => {
    const selectedAssetData = assets.find((asset) => asset.id === value);

    setFormState((prev) => {
      const newState = { ...prev, selectedAsset: value };

      if (!isEditMode && selectedAssetData) {
        const mileageFinal =
          userSettings?.odometer === 'km'
            ? Math.round(selectedAssetData.totalMileage)
            : kmToMiles(Math.round(selectedAssetData.totalMileage));

        const engineHours = Math.round(
          selectedAssetData.engineHourInSeconds / 3600
        );

        newState.startedMileage = mileageFinal;
        newState.startedMileageActual = mileageFinal;
        newState.startedEngineHours = engineHours;
        newState.startedEngineHoursActual = engineHours;

        form.setFieldsValue({
          startingMileage: mileageFinal,
          startingEngineHours: engineHours,
        });
      }

      return newState;
    });
  };

  const handleFormStateChange = (key, value) => {
    setFormState((prev) => ({ ...prev, [key]: value }));
  };

  const handleIntervalChange = (checkedValues) => {
    setSelectedIntervals(checkedValues);

    // Make sure fields are validated when intervals change
    if (checkedValues.includes('time')) {
      form.validateFields(['serviceStartOn']);
    }
    if (checkedValues.includes('mileage')) {
      form.validateFields(['startingMileage', 'intervalMileage']);
    }
    if (checkedValues.includes('engine_hours')) {
      form.validateFields(['startingEngineHours', 'intervalEngineHours']);
    }
  };

  const onFinish = async (values) => {
    let hideLoadingMessage = null;
    try {
      hideLoadingMessage = message.loading('Processing your request...', 0);

      const itemValue =
        values.serviceType === 'PM' ? 'PM' : 'Safety Inspection';
      const isSafetyInspection = values.serviceType !== 'PM';

      const finalEngineHours =
        Math.round(formState.startedEngineHours * 3600) >
        Math.round(formState.startedEngineHoursActual * 3600)
          ? Math.round(formState.startedEngineHours * 3600) -
            Math.round(formState.startedEngineHoursActual * 3600)
          : Math.round(formState.startedEngineHours * 3600);

      const finalStartedMileage =
        userSettings?.odometer === 'km'
          ? Math.round(formState.startedMileage)
          : Math.round(formState.startedMileage * 1.60934);

      const finalStartedMileageActual =
        userSettings?.odometer === 'km'
          ? Math.round(formState.startedMileageActual)
          : Math.round(formState.startedMileageActual * 1.60934);

      const finalMileageInterval =
        userSettings?.odometer === 'km'
          ? Math.round(formState.intervalMileage)
          : Math.round(formState.intervalMileage * 1.60934);

      const start = formState.startedTime;
      const months = Number(formState.repeatInterval);

      const end = start.add(months, 'month').endOf('day');
      const interval_time = end.diff(start, 'second');
      const started_time = start.unix();

      const interval_time_final = values['checkbox-group'].includes('time')
        ? interval_time
        : null;
      const started_time_final =
        values['checkbox-group'].includes('time') && start
          ? started_time
          : null;

      const requestData = {
        shopId: ShopStore.currentShop.id,
        data: {
          isSafetyInspection,
          action: values.serviceName,
          item: itemValue,
          description: values.description || '',
          priority: 1,
          status: 'new',
          actualCarInfo: [
            {
              carId: formState.selectedAsset,
              ...(values['checkbox-group'].includes('mileage') && {
                mileage: finalStartedMileageActual,
              }),
              ...(values['checkbox-group'].includes('engine_hours') && {
                engineHours: Math.round(
                  formState.startedEngineHoursActual * 3600
                ),
              }),
              ...(values['checkbox-group'].includes('time') && {
                time: new Date().toISOString(),
              }),
            },
          ],
          rule_type: createRuleType(values['checkbox-group']),
          interval_time: interval_time_final,
          started_time: started_time_final,
          interval_mileage: values['checkbox-group'].includes('mileage')
            ? finalMileageInterval
            : null,
          started_mileage: values['checkbox-group'].includes('mileage')
            ? finalStartedMileage
            : null,
          interval_engine_hours: values['checkbox-group'].includes(
            'engine_hours'
          )
            ? Math.round(formState.intervalEngineHours * 3600)
            : null,
          started_engine_hours: values['checkbox-group'].includes(
            'engine_hours'
          )
            ? Math.round(finalEngineHours)
            : null,
          threshold: {
            time: values['checkbox-group'].includes('time') ? 1 : null,
            mileage: values['checkbox-group'].includes('mileage') ? 1 : null,
            engineHours: values['checkbox-group'].includes('engine_hours')
              ? 3600
              : null,
          },
        },
        carIds: [formState.selectedAsset],
      };

      if (isEditMode) {
        requestData.issue = { hash: serviceHash };
        if (values['checkbox-group'].includes('mileage')) {
          requestData.issue.car = {
            mileageTotal: Math.round(formState.startedMileage * 1.60934),
          };
        }
      }

      await webServiceProvider.post('v1/car/newPm', requestData);

      if (hideLoadingMessage) hideLoadingMessage();

      notification.success({
        message: 'Service Added Successfully',
        description:
          'The Preventative Maintenance or Safety Inspection has been successfully added.',
      });

      form.resetFields();
      onCancel?.();
    } catch (error) {
      if (hideLoadingMessage) hideLoadingMessage();

      notification.error({
        message: 'Error',
        description:
          error?.response?.data?.message ||
          error.message ||
          'There was an issue processing your request. Please try again.',
      });

      console.error('Submission error:', error);
    }
  };

  return (
    <div style={{ padding: '8px' }}>
      <SetupDetailsHeader>Setup Details</SetupDetailsHeader>

      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
        initialValues={{
          serviceType: initialValues?.serviceType || 'PM',
          description: initialValues?.description || '',
          assetType: formState.assetType,
          asset: formState.selectedAsset,
          'checkbox-group': [],
        }}
      >
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label={<span style={{ fontWeight: 'bold' }}>Service Type:</span>}
              name="serviceType"
              rules={[{ required: true, message: 'Service type is required' }]}
            >
              <Select>
                <Option value="PM">Preventative Maintenance (PM)</Option>
                <Option value="Safety Inspection">DOT Inspection</Option>
              </Select>
            </Form.Item>
          </Col>
          <Col span={12}>
            <ServiceName form={form} initialValues={initialValues} />
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label={<span style={{ fontWeight: 'bold' }}>Asset Type:</span>}
              name="assetType"
              rules={[{ required: true, message: 'Asset type is required' }]}
            >
              <Select onChange={handleAssetTypeChange}>
                <Option value="Vehicle">Vehicle</Option>
                <Option value="Trailer">Trailer</Option>
              </Select>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label={<span style={{ fontWeight: 'bold' }}>Select Asset:</span>}
              name="asset"
              rules={[{ required: true, message: 'Please select an asset' }]}
            >
              <Select
                showSearch
                placeholder="Select an asset"
                onChange={handleAssetChange}
                value={formState.selectedAsset}
                filterOption={(input, option) => {
                  const searchText = `${option?.children}`.toLowerCase();
                  return searchText.includes(input.toLowerCase());
                }}
                optionFilterProp="children"
              >
                {assets.map((asset) => (
                  <Option key={asset.id} value={asset.id}>
                    {`${asset.unitId} - ${asset.vin}`}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <Form.Item
          label={<span style={{ fontWeight: 'bold' }}>Description:</span>}
          name="description"
        >
          <Input.TextArea
            placeholder={
              'Add description such as all service tasks, e.g.:\n1. Oil and filter changes,\n2. Brake inspections and adjustments,\n3. Fluid level checks,\n4. etc.'
            }
            rows={5}
          />
        </Form.Item>

        <Row gutter={16}>
          <Col span={24}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                paddingBottom: '20px',
              }}
            >
              <span style={{ color: 'red', marginLeft: '4px' }}>*</span>
              <span
                style={{
                  fontWeight: 'bold',
                  marginRight: '8px',
                  marginLeft: '8px',
                  textAlign: 'left',
                }}
              >
                Set the service reminder (Select up to 2 reminders e.g., time &
                mileage):
              </span>
              <Tooltip title="Set the service interval based on time, mileage, or engine hours to receive timely reminders and keep your fleet running smoothly">
                <QuestionCircleOutlined />
              </Tooltip>
            </div>

            <Form.Item
              name="checkbox-group"
              rules={[
                { required: true, message: 'Please select an interval base' },
              ]}
              style={{ width: '100%' }}
            >
              <CheckboxGroupWithBorders onChange={handleIntervalChange}>
                <IntervalsRow
                  gutter={16}
                  $isMobileOrTablet={
                    DeviceStore.device === 'mobile' ||
                    DeviceStore.device === 'tablet'
                  }
                >
                  <Col span={8}>
                    <Checkbox
                      value="time"
                      disabled={
                        selectedIntervals.length >= 2 &&
                        !selectedIntervals.includes('time')
                      }
                    >
                      Time
                    </Checkbox>
                    <BlueLine $isActive={selectedIntervals.includes('time')} />
                    <HiddenRow
                      gutter={16}
                      $isVisible={selectedIntervals.includes('time')}
                    >
                      <Col span={10}>
                        <Form.Item
                          label={
                            <span
                              style={{
                                fontWeight: 'bold',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              Service Start On:
                            </span>
                          }
                          name="serviceStartOn"
                          rules={[
                            {
                              required: selectedIntervals.includes('time'),
                              message: 'Please select a start date',
                            },
                          ]}
                        >
                          <DatePicker
                            style={{ width: '100%' }}
                            value={formState.startedTime}
                            onChange={(date) =>
                              handleFormStateChange('startedTime', date)
                            }
                          />
                        </Form.Item>
                      </Col>
                      <Col span={14}>
                        <Form.Item
                          label={
                            <span style={{ fontWeight: 'bold' }}>
                              Repeat Service Every:
                            </span>
                          }
                        >
                          <Input.Group compact style={{ display: 'flex' }}>
                            <Input
                              type="number"
                              min={1}
                              value={formState.repeatInterval}
                              onChange={(e) =>
                                handleFormStateChange(
                                  'repeatInterval',
                                  e.target.value
                                )
                              }
                              style={{ maxWidth: '80px', textAlign: 'center' }}
                            />
                            <Select defaultValue="Months">
                              <Option value="Months">Months</Option>
                            </Select>
                          </Input.Group>
                        </Form.Item>
                      </Col>
                    </HiddenRow>
                  </Col>

                  {/* Mileage Checkbox */}
                  <Col span={8}>
                    <Checkbox
                      value="mileage"
                      disabled={
                        selectedIntervals.length >= 2 &&
                        !selectedIntervals.includes('mileage')
                      }
                    >
                      Mileage
                    </Checkbox>
                    <BlueLine
                      $isActive={selectedIntervals.includes('mileage')}
                    />
                    <HiddenRow
                      gutter={16}
                      $isVisible={selectedIntervals.includes('mileage')}
                    >
                      <Col span={12}>
                        <Form.Item
                          label={
                            <span style={{ fontWeight: 'bold' }}>
                              Starting Mileage:
                            </span>
                          }
                          name="startingMileage"
                          rules={[
                            {
                              required: selectedIntervals.includes('mileage'),
                              message: 'Please enter starting mileage',
                            },
                          ]}
                        >
                          <Input
                            type="number"
                            min={0}
                            value={formState.startedMileage}
                            onChange={(e) =>
                              handleFormStateChange(
                                'startedMileage',
                                e.target.value
                              )
                            }
                            addonAfter={mileOrkm}
                          />
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item
                          label={
                            <span style={{ fontWeight: 'bold' }}>
                              Repeat Mileage Every:
                            </span>
                          }
                          name="intervalMileage"
                          rules={[
                            {
                              required: selectedIntervals.includes('mileage'),
                              message: 'Please enter interval mileage',
                            },
                          ]}
                        >
                          <Input
                            type="number"
                            min={0}
                            value={formState.intervalMileage}
                            onChange={(e) =>
                              handleFormStateChange(
                                'intervalMileage',
                                e.target.value
                              )
                            }
                            addonAfter={mileOrkm}
                          />
                        </Form.Item>
                      </Col>
                    </HiddenRow>
                  </Col>

                  {/* Engine Hours Checkbox */}
                  <Col span={8}>
                    <Checkbox
                      value="engine_hours"
                      disabled={
                        selectedIntervals.length >= 2 &&
                        !selectedIntervals.includes('engine_hours')
                      }
                    >
                      Engine Hours
                    </Checkbox>
                    <BlueLine
                      $isActive={selectedIntervals.includes('engine_hours')}
                    />

                    <HiddenRow
                      gutter={16}
                      $isVisible={selectedIntervals.includes('engine_hours')}
                    >
                      <Col span={12}>
                        <Form.Item
                          label={
                            <span style={{ fontWeight: 'bold' }}>
                              Starting Engine Hours:
                            </span>
                          }
                          name="startingEngineHours"
                          rules={[
                            {
                              required:
                                selectedIntervals.includes('engine_hours'),
                              message: 'Please enter starting engine hours',
                            },
                          ]}
                        >
                          <Input
                            type="number"
                            min={0}
                            value={formState.startedEngineHours}
                            onChange={(e) =>
                              handleFormStateChange(
                                'startedEngineHours',
                                e.target.value
                              )
                            }
                            addonAfter="hrs"
                          />
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item
                          label={
                            <span style={{ fontWeight: 'bold' }}>
                              Repeat Engine Hours Every:
                            </span>
                          }
                          name="intervalEngineHours"
                          rules={[
                            {
                              required:
                                selectedIntervals.includes('engine_hours'),
                              message: 'Please enter interval engine hours',
                            },
                          ]}
                        >
                          <Input
                            type="number"
                            min={0}
                            value={formState.intervalEngineHours}
                            onChange={(e) =>
                              handleFormStateChange(
                                'intervalEngineHours',
                                e.target.value
                              )
                            }
                            addonAfter="hrs"
                          />
                        </Form.Item>
                      </Col>
                    </HiddenRow>
                  </Col>
                </IntervalsRow>
              </CheckboxGroupWithBorders>
            </Form.Item>
          </Col>
        </Row>

        <ServiceIntervalConfirmation
          time={
            selectedIntervals.includes('time') &&
            formState.startedTime &&
            formState.repeatInterval
          }
          mileage={
            selectedIntervals.includes('mileage') && formState.intervalMileage
          }
          hours={
            selectedIntervals.includes('engine_hours') &&
            formState.intervalEngineHours
          }
          isKms={userSettings?.odometer === 'km'}
        />

        <ButtonGroup>
          <Button
            type="link"
            danger
            style={{ color: 'red' }}
            onClick={onCancel}
          >
            Cancel
          </Button>
          <Button type="primary" htmlType="submit">
            Add Service
          </Button>
        </ButtonGroup>
      </Form>
    </div>
  );
};

export default IntervalSelector;
